import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    ChevronDown, Check, Menu, X, Sparkles, Eye,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/api/client";
import { planDescription } from "@/utils/planText";
import { Logo } from "@/components/brand/Logo";
import { T } from "@/components/landing/tokens";
import { FEATURES, type FeatureKey } from "@/components/landing/FeatureDemos";
import { FeatureDemoModal } from "@/components/landing/FeatureDemoModal";


const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/8326848b-3f3a-4f85-9647-2376861e39ce/images/f18677b602bbe1990388ba30ad8413da2ae2cec52cd9546b014adc5d8f073a28.jpeg";

const LANGS = [
    { code: "az", label: "AZ" },
    { code: "en", label: "EN" },
    { code: "ru", label: "RU" },
];

type PublicPlan = {
    code: string;
    name: string;
    description: string | null;
    description_i18n?: Record<string, string> | null;
    price_monthly_minor: number;
    price_annual_minor: number;
    currency: string;
    limits: Record<string, any>;
    modules: string[];
    sort_order: number;
    is_active: boolean;
};

const CURRENCY_SYMBOL: Record<string, string> = { AZN: "₼", USD: "$", EUR: "€", RUB: "₽" };

function formatMinorToMajor(minor: number, currency: string): string {
    const major = Math.round(minor) / 100;
    // Whole-number amounts render without decimals; otherwise 2 decimals.
    const isWhole = Math.abs(major - Math.round(major)) < 0.005;
    const num = isWhole ? String(Math.round(major)) : major.toFixed(2);
    return `${num} ${CURRENCY_SYMBOL[currency] || currency}`;
}

export default function Landing() {
    const { t, i18n } = useTranslation();
    const { status } = useAuth();
    const isAuthed = status === "authenticated";
    const [langOpen, setLangOpen] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [demo, setDemo] = useState<FeatureKey | null>(null);

    const [plans, setPlans] = useState<PublicPlan[] | null>(null);
    const [plansLoading, setPlansLoading] = useState<boolean>(true);
    const [plansError, setPlansError] = useState<boolean>(false);

    useEffect(() => {
        let mounted = true;
        setPlansLoading(true);
        setPlansError(false);
        api.get<{ items: PublicPlan[] }>("/plans/public")
            .then((r) => {
                if (!mounted) return;
                const items = (r.data?.items || []).filter((p) => p.is_active !== false);
                items.sort((a, b) => (a.sort_order || 100) - (b.sort_order || 100));
                setPlans(items);
            })
            .catch(() => {
                if (!mounted) return;
                setPlans([]);
                setPlansError(true);
            })
            .finally(() => { if (mounted) setPlansLoading(false); });
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        const onClick = () => setLangOpen(false);
        if (langOpen) window.addEventListener("click", onClick);
        return () => window.removeEventListener("click", onClick);
    }, [langOpen]);

    const setLang = (code: string) => {
        i18n.changeLanguage(code);
        setLangOpen(false);
    };
    const currentLang = LANGS.find((l) => i18n.language.startsWith(l.code)) || LANGS[0];

    // Which plan is "featured"? The middle one by sort order when we have ≥ 3.
    const featuredIndex = useMemo(() => {
        if (!plans || plans.length < 3) return -1;
        return Math.floor(plans.length / 2);
    }, [plans]);

    return (
        <div className="min-h-screen" style={{ background: T.page, color: T.text, fontFamily: "Inter, 'Noto Sans', system-ui, sans-serif" }} data-testid="landing-page">
            {/* Header */}
            <header className="sticky top-0 z-30 border-b" style={{ background: "rgba(255,255,255,0.9)", backdropFilter: "blur(12px)", borderColor: T.border }} data-testid="landing-header">
                <div className="max-w-[1200px] mx-auto flex items-center gap-6 px-6 py-4">
                    <Link to="/" className="flex items-center gap-2" data-testid="landing-logo" aria-label="Service LAB">
                        <Logo variant="icon" height={40} testId="landing-logo-mark" />
                        <span className="text-base font-semibold tracking-tight" style={{ color: T.text }}>Service LAB</span>
                    </Link>

                    <nav className="hidden md:flex items-center gap-6 mx-auto text-sm" data-testid="landing-nav">
                        <a href="#features" data-testid="nav-features" className="hover:underline" style={{ color: T.textMuted }}>{t("landing.nav.features")}</a>
                        <a href="#pricing" data-testid="nav-pricing" className="hover:underline" style={{ color: T.textMuted }}>{t("landing.nav.pricing")}</a>
                    </nav>

                    <div className="ml-auto flex items-center gap-2">
                        <div className="relative">
                            <button data-testid="lang-btn" onClick={(e) => { e.stopPropagation(); setLangOpen((v) => !v); }} className="inline-flex items-center gap-1 rounded-md border px-3 py-2 text-xs font-medium" style={{ borderColor: T.border, color: T.text, background: T.card }}>
                                {currentLang.label} <ChevronDown size={12}/>
                            </button>
                            {langOpen && (
                                <div className="absolute right-0 mt-1 rounded-md border shadow-md py-1 z-10" style={{ background: T.card, borderColor: T.border, minWidth: 96 }} data-testid="lang-menu">
                                    {LANGS.map((l) => (
                                        <button key={l.code} data-testid={`lang-${l.code}`} onClick={() => setLang(l.code)} className="w-full px-3 py-2 text-left text-xs flex items-center justify-between hover:bg-slate-50" style={{ color: T.text }}>
                                            {l.label} {l.code === currentLang.code && <Check size={12} style={{ color: T.primary }}/>}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        {isAuthed ? (
                            <Link to="/dashboard" data-testid="cta-dashboard" className="hidden sm:inline-flex items-center gap-1 rounded-md px-4 py-2 text-sm font-medium text-white" style={{ background: T.primary, minHeight: 40 }}>{t("landing.nav.go_to_dashboard")}</Link>
                        ) : (
                            <>
                                <Link to="/login" data-testid="cta-login" className="hidden sm:inline-flex items-center rounded-md px-3 py-2 text-sm font-medium" style={{ color: T.text, minHeight: 40 }}>{t("landing.nav.login")}</Link>
                                <Link to="/register" data-testid="cta-register" className="hidden sm:inline-flex items-center rounded-md px-4 py-2 text-sm font-medium text-white" style={{ background: T.primary, minHeight: 40 }}>{t("landing.nav.get_started")}</Link>
                            </>
                        )}
                        <button data-testid="mobile-menu-btn" className="md:hidden inline-flex items-center justify-center rounded-md border p-2" style={{ borderColor: T.border, minWidth: 44, minHeight: 44 }} onClick={() => setMobileOpen((v) => !v)}>
                            {mobileOpen ? <X size={18}/> : <Menu size={18}/>}
                        </button>
                    </div>
                </div>
                {mobileOpen && (
                    <div className="md:hidden border-t px-6 py-3 space-y-2" style={{ borderColor: T.border, background: T.card }} data-testid="mobile-menu">
                        <a href="#features" onClick={() => setMobileOpen(false)} className="block text-sm py-2" style={{ color: T.text }}>{t("landing.nav.features")}</a>
                        <a href="#pricing" onClick={() => setMobileOpen(false)} className="block text-sm py-2" style={{ color: T.text }}>{t("landing.nav.pricing")}</a>
                        {isAuthed ? (
                            <Link to="/dashboard" className="block text-sm py-2 font-medium" style={{ color: T.primary }}>{t("landing.nav.go_to_dashboard")}</Link>
                        ) : (
                            <>
                                <Link to="/login" className="block text-sm py-2" style={{ color: T.text }}>{t("landing.nav.login")}</Link>
                                <Link to="/register" className="block text-sm py-2 font-medium" style={{ color: T.primary }}>{t("landing.nav.get_started")}</Link>
                            </>
                        )}
                    </div>
                )}
            </header>

            {/* Hero */}
            <section className="max-w-[1200px] mx-auto px-6 pt-16 pb-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-testid="hero-section">
                <div>
                    <h1 className="font-bold leading-[1.1] mb-6" style={{ color: T.text, fontSize: "clamp(36px, 5vw, 56px)", letterSpacing: "-0.02em" }} data-testid="hero-title">
                        {t("landing.hero.title")}
                    </h1>
                    <p className="text-lg mb-8 max-w-lg" style={{ color: T.textMuted }} data-testid="hero-subtitle">
                        {t("landing.hero.subtitle")}
                    </p>
                    <div className="flex gap-3 flex-wrap">
                        <Link to={isAuthed ? "/dashboard" : "/register"} data-testid="hero-cta-primary" className="inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-medium text-white transition-colors" style={{ background: T.primary, minHeight: 48 }} onMouseOver={(e) => (e.currentTarget.style.background = T.primaryHover)} onMouseOut={(e) => (e.currentTarget.style.background = T.primary)}>
                            <Sparkles size={16}/> {isAuthed ? t("landing.nav.go_to_dashboard") : t("landing.hero.cta_primary")}
                        </Link>
                        <a href="#pricing" data-testid="hero-cta-secondary" className="inline-flex items-center rounded-md px-6 py-3 text-sm font-medium border" style={{ borderColor: T.border, color: T.text, background: T.card, minHeight: 48 }}>
                            {t("landing.hero.cta_secondary")}
                        </a>
                    </div>
                </div>
                <div className="relative" data-testid="hero-visual">
                    <div className="rounded-2xl overflow-hidden shadow-2xl" style={{ boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.25)" }}>
                        <img src={HERO_IMAGE} alt={t("landing.hero.image_alt")} className="w-full h-auto block" />
                    </div>
                    <div className="absolute bottom-6 left-6 rounded-xl border px-4 py-3 shadow-lg" style={{ background: T.card, borderColor: T.border }} data-testid="hero-stat-card">
                        <div className="text-xs mb-1" style={{ color: T.textMuted }}>{t("landing.hero.stat_label")}</div>
                        <div className="text-2xl font-semibold tabular-nums" style={{ color: T.text }}>{t("landing.hero.stat_value")}</div>
                        <div className="text-xs mt-1" style={{ color: "#15803D" }}>{t("landing.hero.stat_delta")}</div>
                    </div>
                </div>
            </section>

            {/* Features */}
            <section id="features" className="border-t" style={{ borderColor: T.border, background: T.page }} data-testid="features-section">
                <div className="max-w-[1200px] mx-auto px-6 py-20">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="features-grid">
                        {FEATURES.map(({ key, Icon }, i) => (
                            <button type="button" key={key} onClick={() => setDemo(key)} aria-haspopup="dialog" aria-label={`${t(`landing.features.${key}.title` as any)} — ${t("landing.demo.open")}`}
                                className="group relative overflow-hidden rounded-2xl border p-7 text-left cursor-pointer outline-none transition-[transform,box-shadow,border-color] duration-300 ease-out hover:-translate-y-1 hover:shadow-[0_20px_40px_-18px_rgba(124,58,237,0.35)] hover:border-violet-300 focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-2" style={{ borderColor: T.border, background: T.card, boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 4px 14px -6px rgba(15,23,42,0.08)" }} data-testid={`feature-${key}`}>
                                <div className="absolute inset-x-0 top-0 h-[3px] origin-left scale-x-0 transition-transform duration-500 group-hover:scale-x-100" style={{ background: `linear-gradient(90deg, ${T.primary}, ${T.accent})` }} />
                                <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100" style={{ background: T.primarySoft }} />
                                <div className="relative flex items-start justify-between mb-5">
                                    <div className="inline-flex items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-3" style={{ background: `linear-gradient(135deg, ${T.primarySoft}, #FFFFFF)`, width: 52, height: 52, boxShadow: `inset 0 0 0 1px ${T.accent}66` }}>
                                        <Icon size={22} style={{ color: T.primary }}/>
                                    </div>
                                    <span className="text-xs font-semibold tabular-nums tracking-wider transition-opacity duration-200 group-hover:opacity-0" style={{ color: T.accent }}>{String(i + 1).padStart(2, "0")}</span>
                                    <span className="absolute right-0 top-0 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium opacity-0 translate-y-1 transition-[opacity,transform] duration-300 group-hover:opacity-100 group-hover:translate-y-0 group-focus-visible:opacity-100" style={{ background: T.primarySoft, color: T.primary }} data-testid={`feature-${key}-demo-badge`}>
                                        <Eye size={12} />{t("landing.demo.badge")}
                                    </span>
                                </div>
                                <h3 className="relative text-lg font-semibold mb-2 tracking-tight" style={{ color: T.text }}>{t(`landing.features.${key}.title` as any)}</h3>
                                <p className="relative text-sm leading-relaxed" style={{ color: T.textMuted }}>{t(`landing.features.${key}.desc` as any)}</p>
                            </button>
                        ))}
                    </div>
                    <FeatureDemoModal active={demo} onChange={setDemo} isAuthed={isAuthed} />
                </div>
            </section>

            {/* Pricing — dynamic from GET /api/plans/public */}
            <section id="pricing" className="border-t" style={{ borderColor: T.border }} data-testid="pricing-section">
                <div className="max-w-[1200px] mx-auto px-6 py-20">
                    <div className="text-center max-w-2xl mx-auto mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{ color: T.text, letterSpacing: "-0.02em" }} data-testid="pricing-title">
                            {t("landing.pricing.title")}
                        </h2>
                    </div>

                    {plansLoading && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto" data-testid="pricing-loading">
                            {[0, 1, 2].map((i) => (
                                <div key={i} className="rounded-2xl border p-6 animate-pulse" style={{ borderColor: T.border, background: T.card, minHeight: 320 }}>
                                    <div className="h-5 w-1/2 rounded mb-4" style={{ background: "#E2E8F0" }} />
                                    <div className="h-10 w-1/3 rounded mb-6" style={{ background: "#E2E8F0" }} />
                                    <div className="space-y-2">
                                        {[0, 1, 2, 3].map((j) => (
                                            <div key={j} className="h-3 w-full rounded" style={{ background: "#E2E8F0" }} />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {!plansLoading && plansError && (
                        <div className="rounded-xl border p-8 text-center max-w-xl mx-auto" style={{ borderColor: T.border, background: T.card }} data-testid="pricing-error">
                            <p className="text-sm" style={{ color: T.textMuted }}>{t("landing.pricing.error")}</p>
                        </div>
                    )}

                    {!plansLoading && !plansError && plans && plans.length === 0 && (
                        <div className="rounded-xl border p-8 text-center max-w-xl mx-auto" style={{ borderColor: T.border, background: T.card }} data-testid="pricing-empty">
                            <p className="text-sm" style={{ color: T.textMuted }}>{t("landing.pricing.empty")}</p>
                        </div>
                    )}

                    {!plansLoading && !plansError && plans && plans.length > 0 && (
                        <div className={`grid grid-cols-1 gap-6 max-w-4xl mx-auto ${plans.length >= 3 ? "md:grid-cols-3" : plans.length === 2 ? "md:grid-cols-2" : "md:grid-cols-1"}`} data-testid="pricing-grid">
                            {plans.map((p, idx) => {
                                const featured = idx === featuredIndex;
                                // Build feature bullets from limits + modules. Description is
                                // rendered separately as a subtitle just under the plan name.
                                const bullets: string[] = [];
                                if (p.limits) {
                                    const maxBranches = p.limits.max_branches;
                                    const maxStudents = p.limits.max_active_students ?? p.limits.max_students;
                                    const maxUsers = p.limits.max_users;
                                    if (maxBranches != null) bullets.push(t("landing.pricing.limit.branches", { count: maxBranches }));
                                    if (maxStudents != null) bullets.push(t("landing.pricing.limit.students", { count: maxStudents }));
                                    if (maxUsers != null) bullets.push(t("landing.pricing.limit.users", { count: maxUsers }));
                                }
                                // Then, always append active modules (translated via nav.<code> when available).
                                // Phase UI-2: legacy/internal codes that read as raw English (e.g.
                                // "course" — the activity_type sentinel that got persisted into the
                                // plan module list) must never leak into the pricing card. If we
                                // don't have a human i18n string for a module code we drop it
                                // rather than showing the raw slug.
                                const HIDDEN_PLAN_MODULES = new Set(["course"]);
                                const modBullets = (p.modules || [])
                                    .filter((m) => !HIDDEN_PLAN_MODULES.has(m))
                                    .map((m) => {
                                        const key = `nav.${m}` as const;
                                        const label = t(key as any);
                                        // Only keep entries with a real translation.
                                        return label && label !== key ? String(label) : null;
                                    })
                                    .filter((b): b is string => !!b)
                                    .slice(0, 6);
                                for (const b of modBullets) {
                                    if (bullets.length >= 8) break;
                                    if (!bullets.includes(b)) bullets.push(b);
                                }
                                return (
                                    <div
                                        key={p.code}
                                        className={`rounded-2xl border p-6 relative flex flex-col ${featured ? "shadow-lg" : ""}`}
                                        style={{ borderColor: featured ? T.primary : T.border, background: T.card, borderWidth: featured ? 2 : 1 }}
                                        data-testid={`plan-${p.code}`}
                                    >
                                        {featured && (
                                            <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-semibold px-3 py-1 rounded-full text-white" style={{ background: T.primary }} data-testid="popular-badge">
                                                {t("landing.pricing.popular")}
                                            </span>
                                        )}
                                        <h3 className="text-xl font-semibold mb-1" style={{ color: T.text }} data-testid={`plan-name-${p.code}`}>{p.name}</h3>
                                        {planDescription(p, i18n.language) && (
                                            <p className="text-xs mb-1" style={{ color: T.textMuted }} data-testid={`plan-desc-${p.code}`}>{planDescription(p, i18n.language)}</p>
                                        )}
                                        <div className="mb-6 mt-2">
                                            <span className="text-4xl font-bold tabular-nums" style={{ color: T.text }} data-testid={`plan-price-${p.code}`}>
                                                {formatMinorToMajor(p.price_monthly_minor, p.currency || "AZN")}
                                            </span>
                                            <span className="text-sm ml-1" style={{ color: T.textMuted }}>{t("landing.pricing.per_month")}</span>
                                        </div>
                                        {bullets.length > 0 && (
                                            <ul className="space-y-2 mb-8 flex-1" data-testid={`plan-features-${p.code}`}>
                                                {bullets.map((b, i) => (
                                                    <li key={`${p.code}-b-${i}`} className="flex items-start gap-2 text-sm" style={{ color: T.textMuted }}>
                                                        <Check size={16} style={{ color: T.primary, flexShrink: 0, marginTop: 2 }}/>
                                                        <span>{b}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                        <Link to={isAuthed ? "/dashboard" : "/register"} data-testid={`plan-cta-${p.code}`} className={`w-full inline-flex justify-center items-center rounded-md px-4 py-2.5 text-sm font-medium ${featured ? "text-white" : "border"}`} style={featured ? { background: T.primary, minHeight: 44 } : { borderColor: T.border, color: T.text, minHeight: 44 }}>
                                            {t("landing.pricing.cta")}
                                        </Link>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t" style={{ borderColor: T.border, background: T.card }} data-testid="landing-footer">
                <div className="max-w-[1200px] mx-auto px-6 py-8 flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-2">
                        <Logo variant="icon" height={28} testId="landing-footer-logo" />
                        <span className="text-sm" style={{ color: T.textMuted }}>{t("landing.footer.copyright")}</span>
                    </div>
                    <div className="text-xs" style={{ color: T.textMuted }}>
                        {LANGS.map((l, i) => (
                            <span key={l.code}>
                                <button data-testid={`footer-lang-${l.code}`} onClick={() => setLang(l.code)} className="hover:underline" style={{ color: l.code === currentLang.code ? T.primary : T.textMuted, fontWeight: l.code === currentLang.code ? 600 : 400 }}>{l.label}</button>
                                {i < LANGS.length - 1 && <span className="mx-2">·</span>}
                            </span>
                        ))}
                    </div>
                </div>
            </footer>
        </div>
    );
}
